import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';

import {
  BillableIcon,
  CalendarIcon,
  ChevronDownIcon,
  CloudDownloadIcon,
  FileIcon,
  LockedIcon,
  MyTaskIcon,
  TimeSheetIcon,
  UserIcon,
  WorkspaceIcon,
} from '@sketchnote/icons';
import { Image, localeDateOptions, TextCycler } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import cn from 'classnames';
import format from 'date-fns/format';
import formatDuration from 'date-fns/formatDuration';
import intervalToDuration from 'date-fns/intervalToDuration';
import isSameDay from 'date-fns/isSameDay';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { PageErrorBoundary } from 'src/components/error-boundary/page-error-boundary';
import { PLANRULES } from 'src/constants/plans';
import { withAuth } from 'src/hoc';
import { OverviewLayout } from 'src/layouts/overview';
import { useAuth } from 'src/shared/hooks/use-auth';
import { getAllOrgMembers } from 'src/utils/getMemberDetails';

import TableFilters, { DateRangeValue, FilterOption, FilterValue } from '../components/AGFilters';
import { useTimesheetStore } from '../store/useTimesheetStore';
import styles from '../styles/TimeSheetList.module.css';
import { getRowGroupedData, timePeriodFormatter } from '../utils';

interface OrgMember {
  _id: string;
  firstname: string;
  lastname: string;
  email: string;
  role: string;
  avatar: string;
}

type GroupBy = 'tasks' | 'users' | 'default';

interface DropdownOption {
  value: GroupBy;
  label: string;
}

export const TimesheetsPage = withAuth(() => {
  const { getGlobalTimesheet, globalTimelogs, loadingLogs } = useTimesheetStore();
  const [rowData, setRowData] = useState<AgGridReactProps['rowData']>([]);
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const gridRef = useRef<any>(null);
  const [totalLoggedTime, setTotalLoggedTime] = useState(0);
  const [totalBilledTime, setTotalBilledTime] = useState(0);
  const [totalUnbilledTime, setTotalUnbilledTime] = useState(0);
  const authData = useAuth((s) => s.userDetails);
  const [timeFormat, setTimeFormat] = useState<'colon' | 'hm'>('hm');
  const [groupBy, setGroupBy] = useState<GroupBy>('default');
  const BILLABLE = 'Billable';
  const NONBILLABLE = 'Non-Billable';

  const [filterValues, setFilterValues] = useState<Record<string, any>>({});

  const filterDefs = useMemo(() => {
    const workspaceOptions = [...new Set(rowData?.map((r) => r.workspace).filter(Boolean))].map(
      (item) => {
        return {
          label: item,
          value: item,
        };
      }
    );
    const fileOptions = [...new Set(rowData?.map((r) => r.file).filter(Boolean))].map((item) => {
      return {
        label: item,
        value: item,
      };
    });
    const assigneeOptions = [
      ...new Set(
        rowData
          ?.map((r) => r.assignee)
          .flat()
          .filter(Boolean)
      ),
    ].map((item) => {
      return {
        label: item,
        value: item,
      };
    });

    const loggedByOptions = [
      ...new Set(
        rowData
          ?.map((r) => r.member)
          .flat()
          .filter(Boolean)
      ),
    ].map((item) => {
      return {
        label: item,
        value: item,
      };
    });

    const filterOptions: FilterOption[] = [
      {
        filterId: 'tasks',
        filterLabel: 'Task name',
        filterType: 'text',
        filterIcon: <MyTaskIcon />,
      },
      {
        filterId: 'workspace',
        filterLabel: 'Workspace',
        filterType: 'select',
        filterOptions: workspaceOptions,
        filterIcon: <WorkspaceIcon />,
        includeUnassigned: true,
      },
      {
        filterId: 'file',
        filterLabel: 'File',
        filterType: 'select',
        filterOptions: fileOptions,
        filterIcon: <FileIcon />,
      },
      {
        filterId: 'assignee',
        filterLabel: 'Assignee',
        filterType: 'select',
        filterOptions: assigneeOptions,
        includeUnassigned: true,
        filterIcon: <UserIcon />,
      },
      {
        filterId: 'daily_log',
        filterLabel: 'Logged time (mins)',
        filterType: 'numberRange',
        nodeValueModifier: (value) => value / (1000 * 60),
        filterIcon: <TimeSheetIcon />,
      },
      {
        filterId: 'member',
        filterLabel: 'Logged by',
        filterType: 'select',
        filterOptions: loggedByOptions,
        filterIcon: <UserIcon />,
      },
      {
        filterId: 'loggedAt',
        filterLabel: 'Date',
        filterType: 'dateRange',
        filterIcon: <CalendarIcon />,
      },
      {
        filterId: 'billingType',
        filterLabel: 'Billing type',
        filterType: 'select',
        filterOptions: [
          { value: BILLABLE, label: 'Billable' },
          { value: NONBILLABLE, label: 'Non-Billable' },
        ],
        filterIcon: <BillableIcon fill="currentColor" />,
      },
    ];
    return filterOptions;
  }, [rowData]);

  const handleFilterNodes = (node: any) => {
    for (const filterId in filterValues) {
      const filterOption = filterDefs.find((f) => f.filterId === filterId);
      const filterValue = filterValues[filterId];
      if (!filterOption || !filterValue) {
        return true;
      }
      const filterType = filterOption.filterType;
      const filterColumn = filterOption.filterId;
      const nodeValue = node.data[filterColumn];
      if (filterType === 'text') {
        if (!nodeValue.toLowerCase().includes(filterValue.toLowerCase())) {
          return false;
        }
      }
      if (filterType === 'select') {
        if (Array.isArray(nodeValue)) {
          if (!filterValue.some((i) => nodeValue.includes(i))) {
            if (
              !(
                filterOption.includeUnassigned &&
                filterValue.includes('unassigned') &&
                !nodeValue.length
              )
            )
              return false;
          }
        } else if (!filterValue.includes(nodeValue)) {
          if (!(filterOption.includeUnassigned && filterValue.includes('unassigned') && !nodeValue))
            return false;
        }
      }
      if (filterType === 'numberRange') {
        const nodeValue =
          filterOption.nodeValueModifier?.(node.data[filterColumn]) || node.data[filterColumn];
        if (
          (filterValue.min && nodeValue < filterValue.min) ||
          (filterValue.max && nodeValue > filterValue.max)
        ) {
          return false;
        }
      }
      if (filterType === 'dateRange') {
        const nodeValue = new Date(node.data[filterColumn]);
        const dateFilterValue = filterValue as DateRangeValue;
        if (!nodeValue && (!filterOption.includeUnassigned || !dateFilterValue.showUnassigned))
          return false;
        const minDate = dateFilterValue.startDate ? new Date(dateFilterValue.startDate) : null;
        const maxDate = dateFilterValue.endDate ? new Date(dateFilterValue.endDate) : null;
        if ((minDate && nodeValue < minDate) || (maxDate && nodeValue > maxDate)) {
          return false;
        }
      }
    }
    return true;
  };

  const isAllowedForUserPlan = useMemo(() => {
    const memberRole = authData?.memberInformation.role;
    if (memberRole === 'guest') return false;
    return authData?.plan.type ? PLANRULES.globalTimeSheets[authData?.plan.type] : false;
  }, []);

  if (!isAllowedForUserPlan) {
    return (
      <OverviewLayout>
        <PageErrorBoundary wrapperClass="mt-32">
          <div className="w-full flex justify-center my-200">
            <div className="bg-white rounded-sm shadow-sm p-16 inline-flex flex-col">
              <LockedIcon className="m-auto text-gray-400 mb-16" size={20} />
              <p className=" text-gray-500">
                This feature is locked in your current plan. Upgrade to Sketchnote Advanced to
                unlock.
              </p>
            </div>
          </div>
        </PageErrorBoundary>
      </OverviewLayout>
    );
  }

  useEffect(() => {
    getGlobalTimesheet();
    setOrgMembers(getAllOrgMembers());
  }, []);

  const millisecondsToDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor((seconds % 3600) / 60);
    const hours = Math.floor(seconds / 3600);
    const result = {
      hours: hours.toString(),
      minutes: minutes.toString(),
      seconds: (seconds % 60).toString(),
    };
    return result;
  };

  const getLoggedTimeString = (ms: number) => {
    const totalDuration = millisecondsToDuration(ms);
    const { hours = '0', minutes = '0', seconds = '0' } = totalDuration;
    if (timeFormat === 'hm') {
      if (hours === '0' && minutes === '0') return `${seconds}s`;
      if (hours === '0') return `${minutes}m`;
      else if (minutes === '0') return `${hours}h`;
      else return `${hours}h ${minutes}m`;
    } else {
      if (hours === '0' && minutes === '0') return `00:00:${seconds.padStart(2, '0')}`;
      else
        return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`;
    }
  };

  useEffect(() => {
    const filteredLogs = globalTimelogs
      .map((i) => [
        Object.assign({}, i, { parent: null }),
        ...i.subtasks.map((j) =>
          Object.assign({}, j, { workspace: i.workspace, file: i.file, parent: i.id })
        ),
      ])
      .flat()
      .filter((i) => i.logs?.length > 0);
    const data = getRowGroupedData(filteredLogs, groupBy, handleFilterNodes);
    setRowData(data);
  }, [globalTimelogs, groupBy, filterValues]);

  const columnDefinitions: AgGridReactProps['columnDefs'] = [
    // {
    //   field: 'tasks',
    //   cellStyle: (params) => {
    //     if (params.data.parent) {
    //       return {
    //         fontStyle: 'italic',
    //         color: '#999',
    //       };
    //     }
    //   },
    // },
    {
      field: 'workspace',
      headerName: 'Workspace',
    },
    {
      field: 'file',
      headerName: 'File',
    },
    {
      field: 'assignee',
      headerName: 'Assignee',
      valueGetter: ({ data }) => {
        return data?.assignee;
      },
    },
    {
      field: 'daily_log',
      headerName: timeFormat === 'colon' ? 'Logged time (h:m:s)' : 'Logged time',
      cellStyle: { fontWeight: 'bold', textAlign: timeFormat === 'colon' ? 'right' : 'left' },
      width: 190,
      valueFormatter: ({ value }) => getLoggedTimeString(value),
    },
    {
      field: 'member',
      headerName: 'Logged by',
      valueGetter: ({ data }) => {
        return data?.member;
      },
    },
    {
      field: 'loggedAt',
      headerName: 'Date',
      valueFormatter: ({ value }) => {
        return value ? format(new Date(value), 'do MMM yyyy') : '';
      },
    },
    {
      field: 'timePeriod',
      headerName: 'Time Period',
      valueFormatter: ({ value }) => {
        return value?.length === 2 ? timePeriodFormatter(value) : '';
      },
    },
    {
      field: 'billingType',
      headerName: 'Billing type',
      // filter: 'agSetColumnFilter',
      valueGetter: ({ data }) => {
        return data?.billingType;
      },
      cellStyle: (params) => {
        if (params?.data?.billable !== null) {
          return {
            backgroundColor: params?.data?.billable ? '#D1FAE5' : '#FEF4C7',
            background: params?.data?.billable ? '#D1FAE5' : '#FEF4C7',
            color: params?.data?.billable ? '#047857' : '#B45309',
          };
        } else if (params?.data?.billable === null) {
          if (params?.data?.billingType.length > 1) {
            return {
              backgroundColor: 'transparent',
              background:
                'linear-gradient(120deg, rgba(209,250,229,1) 0%, rgba(209,250,229,1) 50%, rgba(254,244,199,1) 50%, rgba(254,244,199,1) 100%)',
              color: 'inherit',
            };
          } else if (params?.data?.billingType[0] === BILLABLE) {
            return {
              backgroundColor: '#D1FAE5',
              background: '#D1FAE5',
              color: '#047857',
            };
          } else if (params?.data?.billingType[0] === NONBILLABLE) {
            return {
              backgroundColor: '#FEF4C7',
              background: '#FEF4C7',
              color: '#B45309',
            };
          }
        }
      },
    },
    // { field: 'billing', headerName: 'Billing Type' },
    // { field: 'action', headerName: 'Action' },
  ];

  const autoGroupColumnDef: ColDef = useMemo(() => {
    return {
      headerName: 'Task logs',
      valueGetter: ({ data }) => {
        return data?.tasks || '';
      },
      lockPosition: true,
      cellRendererParams: {
        suppressCount: true,
      },
      cellStyle: (params) => {
        if (params.data?.parent) {
          return {
            fontStyle: 'italic',
            color: '#999',
          };
        }
      },
    };
  }, []);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    if (gridRef.current) {
      let totalTime = 0,
        billedTime = 0,
        unbilledTime = 0;
      gridRef.current?.api?.forEachNode((node) => {
        if (node.data.isLog) {
          totalTime += node.data.loggedTime;
        }
        if (node.data.isLog && node.data.billable) {
          billedTime += node.data.loggedTime;
        }
        if (node.data.isLog && !node.data.billable) {
          unbilledTime += node.data.loggedTime;
        }
      });
      setTotalLoggedTime(totalTime);
      setTotalBilledTime(billedTime);
      setTotalUnbilledTime(unbilledTime);
    }
  }, []);

  const TimeBoxBase = ({
    time,
    title,
    type,
  }: {
    time: number;
    title: string;
    type: 'total' | 'billed' | 'unbilled';
  }) => {
    const [showFormatted, setShowFormatted] = useState(false);
    const duration = millisecondsToDuration(time);
    const { hours = '0', minutes = '0' } = duration;
    const interval = { start: 0, end: time };
    const intervalDuration = intervalToDuration(interval);
    const formattedDuration = formatDuration(intervalDuration)
      .replace(' years', 'y')
      .replace(' year', 'y')
      .replace(' months', 'm')
      .replace(' month', 'm')
      .replace(' days', 'd')
      .replace(' day', 'd')
      .replace(' hours', 'h')
      .replace(' hour', 'h')
      .replace(' minutes', 'm')
      .replace(' minute', 'm')
      .replace(' seconds', 's')
      .replace(' second', 's');

    return (
      <button
        onClick={() => setShowFormatted(!showFormatted)}
        className="px-10 py-8 rounded-sm bg-gray-100 hover:bg-gray-130 flex flex-col cursor-pointer mr-12"
      >
        <span className="text-xs text-gray-700">{title}</span>
        <span
          className={cn('font-medium', {
            'text-green-800': type === 'billed',
            'text-orange-700': type === 'unbilled',
            'text-primary-600': type === 'total',
          })}
        >
          {showFormatted ? formattedDuration : `${hours}h ${minutes}m`}
        </span>
      </button>
    );
  };

  const TimeBox = memo(TimeBoxBase);

  const handFilterChanged = useCallback(() => {
    let totalTime = 0,
      billedTime = 0,
      unbilledTime = 0;
    gridRef.current?.api?.forEachNodeAfterFilter((node) => {
      if (node.data?.isLog) {
        totalTime += node.data.loggedTime;
      }
      if (node.data?.isLog && node.data?.billable) {
        billedTime += node.data.loggedTime;
      }
      if (node.data?.isLog && !node.data?.billable) {
        unbilledTime += node.data.loggedTime;
      }
    });
    setTotalLoggedTime(totalTime);
    setTotalBilledTime(billedTime);
    setTotalUnbilledTime(unbilledTime);
  }, []);

  const getDataPath = useMemo(() => {
    return (data) => {
      return data.path;
    };
  }, []);

  const onClickExport = () => {
    if (gridRef.current) {
      gridRef.current.api.exportDataAsCsv({
        processCellCallback: (params) => {
          if (params.column.colId === 'daily_log') {
            return Math.round((params.value / (1000 * 60 * 60)) * 100) / 100;
          }
          if (params.column.colId === 'loggedAt') {
            return params.value
              ? new Date(params.value).toLocaleDateString('en-IN', localeDateOptions)
              : '';
          }
          if (params.column.colId === 'timePeriod') {
            return params.value?.length > 1
              ? `${format(new Date(params.value[0]), 'hh:mm a')} - ${format(
                  new Date(params.value[1]),
                  'hh:mm a'
                )}`
              : '';
          }
          if (params.column.colId === 'ag-Grid-AutoColumn') {
            // Add prefix if it is a log
            if (params.api.getRowNode(params.node.id).data?.isLog && groupBy === 'tasks') {
              return '> ' + params.value;
            }
            return params.value;
          }
          return params.value;
        },
        processHeaderCallback: (params) => {
          if (params.column.colId === 'daily_log') {
            return 'Logged time (Hrs)';
          }
          return params.column.colDef.headerName;
        },
        exportedRows: 'filteredAndSorted',
      });
    }
  };

  const getMainMenuItems = useCallback((params) => {
    switch (params.column.getId()) {
      case 'daily_log':
        return [
          ...params.defaultItems,
          {
            name: 'Switch Format',
            action: () => {
              setTimeFormat((prev) => (prev === 'colon' ? 'hm' : 'colon'));
              // reload rows in ag grid
              gridRef.current.api.refreshCells();
            },
          },
        ];
      default:
        return [...params.defaultItems];
    }
  }, []);

  const handleFilterChange = useCallback((filterId: string, value: FilterValue) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterId]: value,
    }));
  }, []);

  const handleFilterClear = useCallback((filterId: string) => {
    setFilterValues((prev) => {
      const { [filterId]: _, ...rest } = prev;
      return rest;
    });
  }, []);
  const dropDownOptions: DropdownOption[] = [
    { value: 'default', label: 'Default' },
    { value: 'tasks', label: 'Group by tasks' },
    { value: 'users', label: 'Group by Users' },
  ];

  const handleGrouping = ({ value }) => {
    setGroupBy(value);
  };

  return (
    <OverviewLayout>
      <PageErrorBoundary wrapperClass="mt-32">
        <>
          <div className="bg-white z-20 w-full overflow-hidden border-b-2 border-gray-200">
            <div className="flex w-90p mx-auto items-center justify-between">
              <div className="px-20 flex items-center">
                <div className="my-24">
                  <div className="flex items-center">
                    <TimeSheetIcon className="items-center" size={22} />
                    <div className="flex w-full items-center">
                      <input
                        className="text-2xl font-semibold mx-12 bg-white w-full"
                        value={'Timesheets'}
                        disabled
                      />
                    </div>
                  </div>
                  <p className="font-regular text-xs text-gray-500">
                    View all time logs within the organization
                  </p>
                </div>
              </div>
              <div className="flex items-center pr-20">
                <div className="flex border-r border-dashed border-primary-500 mr-12">
                  <TimeBox time={totalBilledTime} type="billed" title="Billable" />
                  <TimeBox time={totalUnbilledTime} type="unbilled" title="Non-billable" />
                  <TimeBox time={totalLoggedTime} type="total" title="Total time log" />
                </div>
                <button className="flex items-center mr-4" onClick={onClickExport}>
                  <Tippy content={<span className="text-xs">Export as CSV</span>}>
                    <div className="flex w-full h-full justify-center items-center cursor-pointer text-primary-500 hover:text-primary-700">
                      <CloudDownloadIcon />
                    </div>
                  </Tippy>
                </button>
              </div>
            </div>
          </div>
          <div className="p-16">
            <div className="flex items-center m-auto justify-between" style={{ width: '90%' }}>
              <Select
                className="bg-white w-200 z-10 p-2 text-xs text-primary-600"
                styles={{
                  singleValue: (base) => ({
                    ...base,
                    color: '#254DDA',
                    fontWeight: 500,
                  }),
                  control: (baseStyles) => ({
                    ...baseStyles,
                    paddingRight: '8px',
                    border: '0px solid #254DDAEF',
                    color: '#254DDA',
                    fontSize: '14px',
                  }),
                }}
                isSearchable={false}
                placeholder={'Select an option'}
                value={dropDownOptions.find((i) => i.value === groupBy)}
                options={dropDownOptions}
                onChange={handleGrouping}
                components={{
                  DropdownIndicator: () => (
                    <ChevronDownIcon className=" text-primary-500" fill={'#254DDA'} />
                  ),
                  IndicatorSeparator: () => null,
                }}
              />
              <div>
                <TableFilters
                  filterOptions={filterDefs}
                  onFilterChange={handleFilterChange}
                  activeFilters={filterValues}
                  onFilterClear={handleFilterClear}
                  onClearAllFilters={() => setFilterValues({})}
                />
              </div>
            </div>
            <div
              className={`ag-theme-alpine m-auto relative mt-12 ${styles['custom-ag-theme']}`}
              style={{ height: 'calc((100vh - 245px)', width: '90%' }}
            >
              {loadingLogs ? (
                <div className="w-full text-center py-30">
                  <Image
                    className="m-auto w-200"
                    src={'https://res.cloudinary.com/samvibes/image/upload/Loader/searching.gif'}
                    alt={'Loading'}
                  />
                  <TextCycler
                    texts={['Loading tasks...', 'Calculating time logs...', 'Almost there...']}
                    delayBetweenStrings={5000}
                    className="text-gray-600 mt-12 animate-pulse"
                  />
                </div>
              ) : (
                <AgGridReact
                  rowData={rowData}
                  ref={gridRef}
                  animateRows={true}
                  onGridReady={onGridReady}
                  defaultColDef={{
                    resizable: true,
                  }}
                  isExternalFilterPresent={() => Object.keys(filterValues).length > 0}
                  doesExternalFilterPass={handleFilterNodes}
                  gridOptions={{
                    onFilterChanged: handFilterChanged,
                    // getRowStyle: (params) => {
                    //   if (params.data.isLog) {
                    //     return {
                    //       backgroundColor: '#f5f5f5',
                    //     };
                    //   }
                    // },
                    localeText: {
                      selectAll: 'Select All',
                      notContains: 'Does not contain',
                    },
                  }}
                  getMainMenuItems={getMainMenuItems}
                  overlayLoadingTemplate={
                    '<div style="height:100px; width:100px; background: url(https://ag-grid.com/images/ag-grid-loading-spinner.svg) center / contain no-repeat; margin: 0 auto;" aria-label="loading"></div>'
                  }
                  overlayNoRowsTemplate={
                    '<div class="flex flex-col items-center justify-center"><img class="mb-16 w-400" src="https://res.cloudinary.com/samvibes/image/upload/empty%20states/analytics.png" alt="lucy_desktoponly" /><div class="text-center"><p class="mb-16 text-blue-800 text-xl font-bold">No logged tasks found!</p></div></div>'
                  }
                  treeData={true}
                  getDataPath={getDataPath}
                  autoGroupColumnDef={autoGroupColumnDef}
                  columnDefs={columnDefinitions}
                  excludeChildrenWhenTreeDataFiltering
                  // rowGroupPanelShow="always"
                />
              )}
            </div>
          </div>
        </>
      </PageErrorBoundary>
    </OverviewLayout>
  );
});
